import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  matchValue?: string;
  matchLabel?: string;
  isLoading?: boolean;
  variant?: "default" | "destructive";
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  matchValue,
  matchLabel,
  isLoading,
  variant = "default",
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const isComposingRef = useRef(false);
  const matchInputId = "confirm-dialog-match-input";

  const requiresMatch = !!matchValue;
  const isMatchValid = !requiresMatch || inputValue === matchValue;

  const handleClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    isComposingRef.current = false;
    setInputValue("");
    onClose();
  }, [isLoading, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!isMatchValid || isLoading) {
      return;
    }
    await onConfirm();
  }, [isLoading, isMatchValid, onConfirm]);

  useEffect(() => {
    if (!open) {
      isComposingRef.current = false;
      setInputValue("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        void handleClose();
      } else if (
        e.key === "Enter" &&
        !(e.isComposing || e.keyCode === 229 || isComposingRef.current) &&
        isMatchValid &&
        !isLoading
      ) {
        void handleConfirm();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, isMatchValid, isLoading, handleClose, handleConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative z-50 w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        {description && (
          <div className="mt-2 text-sm text-muted-foreground">
            {description}
          </div>
        )}

        {requiresMatch && (
          <div className="mt-4 space-y-2">
            <Label htmlFor={matchInputId}>
              {matchLabel ??
                t("actions.confirmMatch", {
                  value: matchValue,
                  defaultValue: `確認のため「${matchValue}」と入力してください`,
                })}
            </Label>
            <Input
              id={matchInputId}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={() => {
                isComposingRef.current = false;
              }}
              placeholder={matchValue}
            />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("actions.cancel")}
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={!isMatchValid || isLoading}
          >
            {confirmText ?? t("actions.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
